import get from 'lodash/get';
import { formatPrice } from './price.js';
import { b as LOCAL_STORAGE_IS_SAME_AS_SHIPPING } from '../localStorage-NWX6x-k6.js';
import { a as ProductType } from '../product-_pRduMz7.js';
import 'number-currency-format';
import './currency.js';
import '../currency-_kacFJHO.js';
import '../language-BHHwgtQp.js';

const getProductNewPrice = (item, currencyCode, language, noZeroAfterDecimal) => {
    const price = item.prices.price.value;
    if (typeof price !== 'number')
        return '';
    return formatPrice(price, currencyCode, language, noZeroAfterDecimal);
};
const getProductOldPrice = (item, currencyCode, language, noZeroAfterDecimal) => {
    var _a;
    const price = item.prices.price.value;
    if (typeof price !== 'number')
        return '';
    let oldPrice = 0;
    if (item.product.type_id === ProductType.Configurable) {
        oldPrice = item.configured_variant.price_range.minimum_price.regular_price.value;
    }
    else {
        oldPrice = (_a = item.product.price_range) === null || _a === void 0 ? void 0 : _a.minimum_price.regular_price.value;
    }
    return formatPrice(oldPrice, currencyCode, language, noZeroAfterDecimal);
};
const getCartPricesFromCheckoutPrices = (prices) => ({
    original_price: prices.original_price,
    subtotal_including_tax: prices.subtotal_including_tax,
    grand_total: prices.grand_total,
    discounts: prices.discounts,
});
const getIsSameAsShipping = () => localStorage.getItem(LOCAL_STORAGE_IS_SAME_AS_SHIPPING) === 'true';
const getPaymentMethod = (orderData) => {
    const paymentMethod = get(orderData, ['payment_methods', 0, 'name'], '');
    switch (paymentMethod) {
        case 'Check / Money order':
            return '$common.$labels.$paymentMethods.checkMo';
        case 'Credit Cards':
            return '$common.$labels.$paymentMethods.creditCard';
        case 'Bank Transfer':
            return '$common.$labels.$paymentMethods.bankTransfer';
        case 'Points Payment':
            return '$common.$labels.$paymentMethods.rewardPoints';
        default:
            return paymentMethod;
    }
};
const isPointsPayment = (orderData) => {
    const paymentMethod = get(orderData, ['payment_methods', 0, 'name'], '');
    return paymentMethod === 'Points Payment';
};
const formatPoints = (points) => `${points.toLocaleString()} pts`;
/**
 * Safely get points totals from an order with consistent fallback logic.
 * type: 'grand' | 'subtotal'
 */
const getOrderPointsValue = (orderData, type) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!orderData)
        return 0;
    if (type === 'grand') {
        return ((_d = (_a = orderData.points_grand_total) !== null && _a !== void 0 ? _a : (_c = (_b = orderData.total) === null || _b === void 0 ? void 0 : _b.points) === null || _c === void 0 ? void 0 : _c.points_grand_total) !== null && _d !== void 0 ? _d : 0);
    }
    return ((_h = (_e = orderData.points_subtotal) !== null && _e !== void 0 ? _e : (_g = (_f = orderData.total) === null || _f === void 0 ? void 0 : _f.points) === null || _g === void 0 ? void 0 : _g.points_subtotal) !== null && _h !== void 0 ? _h : 0);
};
const mapStatusToTranslation = (status) => {
    switch (status) {
        case 'Closed':
            return '$common.$labels.$status.Closed';
        case 'Shipped':
            return '$common.$labels.$status.Shipped';
        case 'Canceled':
            return '$common.$labels.$status.Canceled';
        case 'Processing':
        case 'Processing Solomon':
            return '$common.$labels.$status.Processing';
        case 'Pending':
        case 'Pending Check':
        case 'Pending Bank Transfer':
        case 'Tried Alma Payment Update':
            return '$common.$labels.$status.Pending';
        default:
            return status;
    }
};
const getStatusColor = (status) => {
    const normalizedStatus = status.toLowerCase();
    if (normalizedStatus.includes('closed') ||
        normalizedStatus.includes('processing') ||
        normalizedStatus.includes('shipped')) {
        return 'success-ui';
    }
    if (normalizedStatus.includes('canceled')) {
        return 'error-ui';
    }
    if (normalizedStatus.includes('pending')) {
        return 'info-ui';
    }
    return 'black';
};
const getPaypalPurchaseUnit = (cart) => {
    var _a;
    const totalPrice = cart.prices.grand_total;
    const currencyCode = totalPrice.currency;
    const totalPriceValue = totalPrice.value;
    const shippingPriceValue = get(cart, ['shipping_addresses', 0, 'selected_shipping_method', 'amount', 'value'], 0);
    const subTotalPriceValue = cart.prices.subtotal_including_tax.value;
    const customDutyPriceValue = ((_a = cart.prices.custom_duty) === null || _a === void 0 ? void 0 : _a.value) || 0;
    const discounts = cart.prices.discounts || [];
    const discountPriceValue = discounts.reduce((acc, val) => acc + val.amount.value, 0);
    const items = cart.items.map(item => {
        const priceValue = item.prices.price.value;
        return {
            name: item.product.name,
            quantity: String(item.quantity),
            unit_amount: {
                currency_code: currencyCode,
                value: String(priceValue),
            },
        };
    });
    return {
        amount: {
            currency_code: currencyCode,
            value: String(totalPriceValue),
            breakdown: {
                item_total: {
                    currency_code: currencyCode,
                    value: String(subTotalPriceValue),
                },
                shipping: {
                    currency_code: currencyCode,
                    value: String(shippingPriceValue),
                },
                tax_total: {
                    currency_code: currencyCode,
                    value: String(customDutyPriceValue),
                },
                discount: {
                    currency_code: currencyCode,
                    value: String(discountPriceValue),
                },
            },
        },
        items,
    };
};
const isAlmaPaymentMethod = (paymentMethodCode) => paymentMethodCode.split('|')[0] === 'alma';
/**
 * Checks if the current page is the checkout success page
 * This prevents cart reactivation on the success page
 * @returns true if on checkout success page, false otherwise
 */
const isCheckoutSuccessPage = () => {
    if (typeof window === 'undefined') {
        return false;
    }
    const currentPath = window.location.pathname;
    return currentPath.includes('/checkout/success');
};

export { formatPoints, getCartPricesFromCheckoutPrices, getIsSameAsShipping, getOrderPointsValue, getPaymentMethod, getPaypalPurchaseUnit, getProductNewPrice, getProductOldPrice, getStatusColor, isAlmaPaymentMethod, isCheckoutSuccessPage, isPointsPayment, mapStatusToTranslation };
//# sourceMappingURL=checkout.js.map
